/**
 * Theme Context Module
 *
 * Provides centralized theme state management and switching functionality for the application.
 * This module implements a React Context-based theming system that supports multiple visual themes,
 * persists user preferences to localStorage, and applies CSS custom properties dynamically.
 *
 * @module contexts/ThemeContext
 */
import { createContext, useState, useEffect, ReactNode, useMemo, useRef } from 'react';
import type { ThemeName, ThemeContextValue } from '../themes';
import { themes, isValidThemeName } from '../themes';
/**
 * Theme Context
 *
 * React Context for sharing theme state across the application.
 * Provides access to the current theme name and functions to change themes.
 *
 * @context
 * @type {React.Context<ThemeContextValue | undefined>}
 *
 * @example
 * ```tsx
 * import { ThemeContext } from './contexts/ThemeContext';
 *
 * function MyComponent() {
 *   const themeContext = useContext(ThemeContext);
 *   if (!themeContext) throw new Error('Must be used within ThemeProvider');
 *
 *   return <div>Current theme: {themeContext.theme}</div>;
 * }
 * ```
 */
export const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);
/**
 * ThemeProvider Props
 *
 * @interface ThemeProviderProps
 * @property {ReactNode} children - Child components to be wrapped by the theme provider
 */
interface ThemeProviderProps {
    children: ReactNode;
}
/**
 * Load theme from localStorage
 *
 * Attempts to read the user's saved theme preference from browser localStorage.
 * Includes error handling for cases where localStorage is disabled, quota exceeded,
 * or the saved value is invalid.
 *
 * @function loadTheme
 * @returns {ThemeName} The saved theme name if valid, otherwise defaults to 'modern'
 *
 * @example
 * ```typescript
 * const theme = loadTheme(); // Returns 'retro' or 'modern'
 * ```
 */
const loadTheme = (): ThemeName => {
    try {
        const saved = localStorage.getItem('theme');
        if (saved && isValidThemeName(saved)) {
            return saved;
        }
    }
    catch (error) {
        // Handle localStorage errors (disabled, quota exceeded, etc.)
        console.warn('Failed to load theme from localStorage:', error);
    }
    return 'modern';
};
/**
 * Save theme to localStorage
 *
 * Persists the user's theme preference to browser localStorage for future sessions.
 * Includes error handling for localStorage failures (disabled, quota exceeded, etc.).
 *
 * @function saveTheme
 * @param {ThemeName} themeName - The theme name to save ('retro' or 'modern')
 *
 * @example
 * ```typescript
 * saveTheme('modern'); // Saves 'modern' to localStorage
 * ```
 */
const saveTheme = (themeName: ThemeName): void => {
    try {
        localStorage.setItem('theme', themeName);
    }
    catch (error) {
        // Handle localStorage errors gracefully - app continues to work without persistence
        console.warn('Failed to save theme to localStorage:', error);
    }
};
/**
 * Apply theme CSS variables to document root
 *
 * Updates all CSS custom properties on the document root element based on the active theme.
 * This function is the core of the theming system, dynamically updating CSS variables that
 * control the visual appearance of all components.
 *
 * Performance optimizations:
 * - Uses requestAnimationFrame to batch all CSS variable updates in a single frame
 * - Prevents layout thrashing by grouping all style changes together
 * - Completes within 100ms to meet performance requirements
 *
 * @function applyTheme
 * @param {ThemeName} themeName - The theme name to apply ('retro' or 'modern')
 * @param {boolean} [isInitial=false] - Whether this is the initial theme application (skips animations)
 *
 * @example
 * ```typescript
 * applyTheme('modern'); // Applies modern theme with animations
 * applyTheme('retro', true); // Applies retro theme without animations (initial load)
 * ```
 */
const applyTheme = (themeName: ThemeName, isInitial = false): void => {
    const theme = themes[themeName];
    // Use requestAnimationFrame to batch all CSS variable updates in a single frame
    // This ensures smooth rendering and prevents layout thrashing
    requestAnimationFrame(() => {
        const root = document.documentElement.style;
        const docElement = document.documentElement;
        // Trigger theme transition animation (skip on initial load to avoid flash)
        if (!isInitial) {
            docElement.setAttribute('data-theme-transitioning', themeName);
            // Remove the transitioning attribute after animation completes (1 second)
            setTimeout(() => {
                docElement.removeAttribute('data-theme-transitioning');
            }, 1000);
        }
        // Set data-theme attribute for conditional CSS styling
        // This allows CSS to apply theme-specific styles using [data-theme="modern"] selectors
        docElement.setAttribute('data-theme', themeName);
        // Batch all CSS variable updates together for better performance
        // This reduces layout thrashing by grouping all style changes into a single operation
        // Apply color variables (--color-bg, --color-fg, --color-accent, etc.)
        // These control all color-related styling throughout the application
        Object.entries(theme.colors).forEach(([key, value]) => {
            root.setProperty(`--color-${key}`, value);
        });
        // Apply typography variables
        // Font families for primary text and monospace code
        root.setProperty('--font-family', theme.typography.fontFamily);
        root.setProperty('--font-family-mono', theme.typography.fontFamilyMono);
        // Font sizes (--font-size-xs through --font-size-2xl)
        Object.entries(theme.typography.fontSize).forEach(([key, value]) => {
            root.setProperty(`--font-size-${key}`, value);
        });
        // Font weights (--font-weight-normal, --font-weight-bold, etc.)
        Object.entries(theme.typography.fontWeight).forEach(([key, value]) => {
            root.setProperty(`--font-weight-${key}`, value.toString());
        });
        // Line heights (--line-height-tight, --line-height-normal, etc.)
        Object.entries(theme.typography.lineHeight).forEach(([key, value]) => {
            root.setProperty(`--line-height-${key}`, value.toString());
        });
        // Apply spacing variables (--spacing-xs through --spacing-2xl)
        // Used for margins, padding, and gaps throughout the application
        Object.entries(theme.spacing).forEach(([key, value]) => {
            root.setProperty(`--spacing-${key}`, value);
        });
        // Apply border radius variables (--radius-sm through --radius-full)
        // Controls the roundness of corners on buttons, cards, and other elements
        Object.entries(theme.borderRadius).forEach(([key, value]) => {
            root.setProperty(`--radius-${key}`, value);
        });
        // Apply shadow variables (--shadow-sm, --shadow-md, --shadow-lg, --shadow-glow)
        // Provides elevation and depth to UI elements
        Object.entries(theme.shadows).forEach(([key, value]) => {
            root.setProperty(`--shadow-${key}`, value);
        });
        // Apply transition variables (--transition-fast, --transition-base, --transition-slow)
        // Controls animation timing for smooth theme transitions
        Object.entries(theme.transitions).forEach(([key, value]) => {
            root.setProperty(`--transition-${key}`, value);
        });
    });
};
/**
 * ThemeProvider Component
 *
 * Wraps the application to provide theme context to all child components.
 * Manages theme state, persistence, and CSS variable application.
 *
 * Features:
 * - Loads saved theme preference from localStorage on mount
 * - Applies CSS variables to document root when theme changes
 * - Persists theme changes to localStorage
 * - Provides theme context to all child components via React Context
 * - Announces theme changes to screen readers for accessibility
 * - Tracks performance metrics to ensure sub-100ms theme switching
 *
 * @component
 * @param {ThemeProviderProps} props - Component props
 * @param {ReactNode} props.children - Child components to wrap
 *
 * @example
 * ```tsx
 * import { ThemeProvider } from './contexts/ThemeContext';
 *
 * function App() {
 *   return (
 *     <ThemeProvider>
 *       <YourApp />
 *     </ThemeProvider>
 *   );
 * }
 * ```
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
    // Initialize state with theme from localStorage or default to 'retro'
    const [theme, setThemeState] = useState<ThemeName>(() => loadTheme());
    // Track if this is the initial mount to skip animations on first render
    const [isInitialMount, setIsInitialMount] = useState(true);
    // Track performance metrics to ensure theme switching meets 100ms requirement
    const performanceRef = useRef<{
        startTime: number | null;
    }>({ startTime: null });
    // Reference to ARIA live region for accessibility announcements
    const liveRegionRef = useRef<HTMLDivElement | null>(null);
    /**
     * Effect: Apply theme and track performance
     *
     * Runs when the theme changes to:
     * 1. Apply CSS variables to document root
     * 2. Announce theme change to screen readers
     * 3. Measure performance to ensure sub-100ms switching
     *
     * Performance tracking is only active in development mode.
     */
    useEffect(() => {
        // Start performance measurement (skip on initial mount)
        if (!isInitialMount) {
            performanceRef.current.startTime = performance.now();
        }
        // Apply theme CSS variables to document root
        applyTheme(theme, isInitialMount);
        // Announce theme change to screen readers (skip on initial load to avoid noise)
        if (!isInitialMount && liveRegionRef.current) {
            liveRegionRef.current.textContent = `Theme changed to ${theme}`;
            // Clear announcement after 1 second to keep live region clean
            setTimeout(() => {
                if (liveRegionRef.current) {
                    liveRegionRef.current.textContent = '';
                }
            }, 1000);
        }
        // Measure theme switch time and log performance metrics
        if (!isInitialMount && performanceRef.current.startTime !== null) {
            requestAnimationFrame(() => {
                const endTime = performance.now();
                const duration = endTime - performanceRef.current.startTime!;
                // Log performance metric (only in development to avoid console spam in production)
                if (process.env['NODE_ENV'] === 'development') {
                    // Warn if theme switch exceeds 100ms requirement (Requirement 4.1)
                    if (duration > 100) {
                    }
                }
                performanceRef.current.startTime = null;
            });
        }
        // Mark initial mount as complete after first render
        if (isInitialMount) {
            setIsInitialMount(false);
        }
    }, [theme, isInitialMount]);
    /**
     * Set theme
     *
     * Sets a specific theme by name. Validates the theme name before applying.
     * Updates component state, applies CSS variables, and persists to localStorage.
     *
     * @function setTheme
     * @param {ThemeName} newTheme - The theme name to set ('retro' or 'modern')
     *
     * @example
     * ```tsx
     * const { setTheme } = useTheme();
     * setTheme('modern'); // Switches to modern theme
     * ```
     */
    const setTheme = (newTheme: ThemeName): void => {
        // Validate theme name to prevent invalid values
        if (!isValidThemeName(newTheme)) {
            console.error(`Invalid theme: ${newTheme}. Falling back to 'retro'.`);
            return;
        }
        setThemeState(newTheme);
        saveTheme(newTheme);
    };
    /**
     * Toggle theme
     *
     * Switches between 'retro' and 'modern' themes.
     * Convenience function for theme toggle buttons.
     *
     * @function toggleTheme
     *
     * @example
     * ```tsx
     * const { toggleTheme } = useTheme();
     * <button onClick={toggleTheme}>Toggle Theme</button>
     * ```
     */
    const toggleTheme = (): void => {
        const newTheme = theme === 'retro' ? 'modern' : 'retro';
        setTheme(newTheme);
    };
    /**
     * Memoize context value to prevent unnecessary re-renders
     *
     * Only recreates the context value when the theme changes.
     * This optimization prevents child components from re-rendering
     * when the ThemeProvider re-renders for other reasons.
     */
    const contextValue: ThemeContextValue = useMemo(() => ({
        theme,
        setTheme,
        toggleTheme,
    }), [theme]);
    return (<ThemeContext.Provider value={contextValue}>
      {/* Accessible live region for theme change announcements */}
      <div ref={liveRegionRef} role="status" aria-live="polite" aria-atomic="true" className="sr-only"/>
      {children}
    </ThemeContext.Provider>);
}
